import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import {
  formatDate, amountInWords, sv, getItems, getTrBoe,
  buildInsuranceLine, buildPackagesLine, placeInsuranceLine, appendRegistrationLine,
  LW, M, FONT,
} from './helpers';

applyPlugin(jsPDF);

// ═══════════════════════════════════════════════════════════════════════════════
// COMMERCIAL INVOICE PDF
// ═══════════════════════════════════════════════════════════════════════════════

const LH = 3.5; // line height at 8pt

// Splits "value" so the first chunk fits on the same line as the bold label and
// the rest wraps to full-width lines below. Returns layout for drawLabelValue.
function splitLabelValue(doc, label, value, maxW) {
  doc.setFontSize(8);
  doc.setFont(FONT, 'bold');
  const labelW = doc.getTextWidth(label) + 1.5;
  doc.setFont(FONT, 'normal');
  const words = String(sv(value)).split(/\s+/).filter(Boolean);
  const firstW = Math.max(maxW - labelW, 10);
  let first = '';
  let i = 0;
  while (i < words.length) {
    const trial = first ? `${first} ${words[i]}` : words[i];
    if (doc.getTextWidth(trial) <= firstW) { first = trial; i++; } else break;
  }
  const rest = words.slice(i).join(' ');
  const lines = rest ? doc.splitTextToSize(rest, maxW) : [];
  return { label, labelW, first, lines, lineCount: 1 + lines.length };
}

function drawLabelValue(doc, lv, x, y) {
  doc.setFontSize(8);
  doc.setFont(FONT, 'bold');
  doc.text(lv.label, x, y);
  doc.setFont(FONT, 'normal');
  if (lv.first) doc.text(lv.first, x + lv.labelW, y);
  lv.lines.forEach((line, idx) => doc.text(line, x, y + (idx + 1) * LH));
}

export function generateInvoicePDF(document, company, authority, returnBlob = false) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2; // content width

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M;

  // ── Title (outside border) ─────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text('C O M M E R C I A L   I N V O I C E', pw / 2, y + 5, { align: 'center' });
  y += 10;

  // borderStartY marks where the page border begins (below the title)
  const borderStartY = y;

  // ── Row 1: Consignor | (Invoice No & Dt + Exporter Ref) / Buyer's order ──
  const col1W = cw * 0.45;
  const invW = cw * 0.35;
  const refW = cw * 0.20;
  const rightW = invW + refW;

  doc.setFontSize(8);
  doc.setFont(FONT, 'bold');
  const exporterNameLines = doc.splitTextToSize(sv(company.exporter_name), col1W - 4);
  doc.setFont(FONT, 'normal');
  const exporterAddrLines = doc.splitTextToSize(
    `${sv(company.exporter_address)} E MAIL:${sv(company.email)}, GSTIN: ${sv(company.gstin)}`,
    col1W - 4
  );
  const exporterLines = [...exporterNameLines, ...exporterAddrLines];

  const invValue = sv(document.invoice_no) +
    (document.document_date ? ` DATED ${formatDate(document.document_date)}` : '');
  const invLv = splitLabelValue(doc, 'Invoice No. & Dt. :', invValue, invW - 4);
  const proforma = document.proforma || {};
  const piValue = (proforma.invoice_no ? 'Proforma Invoice No. ' : '') +
    sv(proforma.invoice_no) +
    (proforma.document_date ? ` DT. ${formatDate(proforma.document_date)}` : '');
  // Value starts on the 2nd line, below the label
  doc.setFontSize(8);
  doc.setFont(FONT, 'normal');
  const boLines = doc.splitTextToSize(piValue, rightW - 4);
  const boLv = {
    label: `Buyer's order no. & Dt. :`,
    labelW: 0,
    first: '',
    lines: boLines,
    lineCount: 1 + boLines.length,
  };

  const topH = Math.max(invLv.lineCount * LH + 5, 12);
  const boH = Math.max(boLv.lineCount * LH + 5, 10);
  const row1H = Math.max(exporterLines.length * LH + 4, topH + boH, 28);

  // Consignor cell (spans full row height)
  doc.rect(M, y, col1W, row1H);
  doc.setFontSize(8);
  doc.setFont(FONT, 'bold');
  doc.text('Consignor/ Exporter :', M + 2, y + 4);
  doc.text(exporterNameLines, M + 2, y + 8);
  doc.setFont(FONT, 'normal');
  doc.text(exporterAddrLines, M + 2, y + 8 + exporterNameLines.length * LH);

  // Invoice No & Dt cell (top strip, middle)
  doc.rect(M + col1W, y, invW, topH);
  drawLabelValue(doc, invLv, M + col1W + 2, y + 4);

  // Exporter Reference cell (top strip, right)
  doc.rect(M + col1W + invW, y, refW, topH);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text('Exporter Reference :', M + col1W + invW + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(`IEC : ${sv(company.iec)}`, M + col1W + invW + 2, y + 8);

  // Buyer's order cell — shows the linked proforma invoice no & date
  doc.rect(M + col1W, y + topH, rightW, row1H - topH);
  drawLabelValue(doc, boLv, M + col1W + 2, y + topH + 4);

  y += row1H;

  // ── Row 2: Consignee/Buyer | Notify ──────────────────────────────────
  const halfW = cw / 2;
  const trBoe = getTrBoe(document);
  const lcBankText = [sv(document.lc_opening_bank), sv(document.lc_opening_bank_address)]
    .filter(Boolean).join(', ');
  // Saved Notify wins; otherwise compose buyer + L/C opening bank
  const notifyText = sv(
    trBoe.notify,
    `${sv(document.buyer_name)}\n${sv(document.buyer_address)}` +
      (lcBankText ? `\nAND ${lcBankText}` : '')
  );
  const bLines = doc.splitTextToSize(
    `${sv(document.buyer_name)}\n${sv(document.buyer_address)}`,
    halfW - 4
  );
  const nLines = doc.splitTextToSize(notifyText, halfW - 4);
  const row2H = Math.max((bLines.length + 1) * LH + 2, (nLines.length + 1) * LH + 2, 16);

  doc.rect(M, y, halfW, row2H);
  doc.setFont(FONT, 'bold');
  doc.text('Consignee/ Buyer :', M + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(bLines, M + 2, y + 8);

  doc.rect(M + halfW, y, halfW, row2H);
  doc.setFont(FONT, 'bold');
  doc.text('Notify :', M + halfW + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(nLines, M + halfW + 2, y + 8);

  y += row2H;

  // ── Row 3: Country | Mode of Shipment | Terms of delivery | Payment terms
  const q1 = cw * 0.22;
  const q2 = cw * 0.28;
  const q3 = cw * 0.25;
  const q4 = cw * 0.25;

  const modeLines = doc.splitTextToSize(sv(document.mode_of_shipment), q2 - 4);
  const termsLines = doc.splitTextToSize(sv(document.terms_of_delivery), q3 - 4);
  const paymentLines = doc.splitTextToSize(sv(document.mode_of_payment), q4 - 4);
  const row3H = Math.max(
    (modeLines.length + 1) * LH + 4,
    (termsLines.length + 1) * LH + 4,
    (paymentLines.length + 1) * LH + 4,
    12
  );

  doc.rect(M, y, q1, row3H);
  doc.setFont(FONT, 'bold');
  doc.text('Country of Origin : INDIA', M + 2, y + 4);

  doc.rect(M + q1, y, q2, row3H);
  doc.setFont(FONT, 'bold');
  doc.text('Mode of Shipment : ', M + q1 + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(modeLines, M + q1 + 2, y + 8);

  doc.rect(M + q1 + q2, y, q3, row3H);
  doc.setFont(FONT, 'bold');
  doc.text('Terms of delivery : ', M + q1 + q2 + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(termsLines, M + q1 + q2 + 2, y + 8);

  doc.rect(M + q1 + q2 + q3, y, q4, row3H);
  doc.setFont(FONT, 'bold');
  doc.text('Payment terms : ', M + q1 + q2 + q3 + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(paymentLines, M + q1 + q2 + q3 + 2, y + 8);

  y += row3H;

  // ── Row 4: Shipment From | Shipment To | L/C details ─────────────────
  const lcParts = [];
  if (sv(document.lc_no)) lcParts.push(sv(document.lc_no));
  if (document.lc_opening_date) lcParts.push(`DATE ${formatDate(document.lc_opening_date)}`);
  if (sv(document.lc_amendment_ref)) {
    let amendment = `AMENDMENT ${sv(document.lc_amendment_ref)}`;
    if (document.lc_amendment_ref_date) amendment += ` DT.${formatDate(document.lc_amendment_ref_date)}`;
    lcParts.push(amendment);
  }
  if (sv(document.lc_opening_bank)) {
    let bank = `ISSUED BY ${sv(document.lc_opening_bank)}`;
    if (sv(document.lc_opening_bank_address)) bank += `, ${sv(document.lc_opening_bank_address)}`;
    lcParts.push(bank);
  }
  const lcLv = splitLabelValue(doc, 'L/C  No. :', lcParts.join(' '), halfW - 4);
  doc.setFontSize(8);
  doc.setFont(FONT, 'bold');
  const fromHeadLines = doc.splitTextToSize('Shipment From/ Port of Loading :', q1 - 4);
  const toHeadLines = doc.splitTextToSize('Shipment To/ Port of Discharge :', q2 - 4);
  doc.setFont(FONT, 'normal');
  const fromLines = doc.splitTextToSize(sv(document.shipment_from), q1 - 4);
  const toLines = doc.splitTextToSize(sv(document.shipment_to), q2 - 4);
  const row4H = Math.max(
    lcLv.lineCount * LH + 6,
    (fromHeadLines.length + fromLines.length) * LH + 5,
    (toHeadLines.length + toLines.length) * LH + 5,
    14
  );

  // Shipment From (left)
  doc.rect(M, y, q1, row4H);
  doc.setFont(FONT, 'bold');
  doc.text(fromHeadLines, M + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(fromLines, M + 2, y + 4 + fromHeadLines.length * LH);

  // Shipment To (beside it)
  doc.rect(M + q1, y, q2, row4H);
  doc.setFont(FONT, 'bold');
  doc.text(toHeadLines, M + q1 + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(toLines, M + q1 + 2, y + 4 + toHeadLines.length * LH);

  // L/C (right half)
  doc.rect(M + halfW, y, halfW, row4H);
  drawLabelValue(doc, lcLv, M + halfW + 2, y + 4);

  y += row4H;

  // ── Items Table ─────────────────────────────────────────────────────
  const items = getItems(document);
  const fobValueUsd = parseFloat(document.fob_value_usd) || 0;
  const freightUsd = parseFloat(document.freight_charges_usd) || 0;

  const itemRows = items.map((item, idx) => {
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate_usd) || 0;
    const amt = parseFloat(item.amount_usd) || (qty * rate);
    const hsCode = sv(item.hs_code) || sv(document.hs_code);
    let desc = sv(item.description_of_goods);
    if (idx === 0) {
      if (fobValueUsd) desc += `\nFOB VALUE : US$${fobValueUsd.toFixed(2)}`;
      if (freightUsd) desc += `\nFREIGHT CHARGES : US$${freightUsd.toFixed(2)}`;
    }
    if (hsCode) desc += `\nH.S. CODE NO. ${hsCode}`;
    return [
      sv(item.marks_and_nos),
      desc,
      qty ? `${qty.toFixed(4)} ${sv(item.uom)}` : sv(item.uom),
      rate ? rate.toFixed(4) : '',
      amt ? amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
    ];
  });

  // Additional notes / declaration — printed verbatim as a full-width row after the last item.
  // Other Reference goes in the footer box below. The insurance cover-note details are
  // injected into whichever of the two blocks carries the buyer's registration line.
  const { declaration: declText, otherRef: refText } = placeInsuranceLine(
    document.declaration, document.other_ref, buildInsuranceLine(document)
  );
  if (declText) {
    itemRows.push([
      { content: declText, colSpan: 5, styles: { fontStyle: 'normal', halign: 'left' } },
    ]);
  }

  doc.autoTable({
    startY: y,
    head: [['Marks & Nos.', 'D e s c r i p t i o n   o f   G o o d s', 'Quantity', 'Rate(USD)', 'Amount(USD)']],
    body: itemRows,
    theme: 'grid',
    styles: { fontSize: 8, font: FONT, textColor: [0, 0, 0], lineWidth: LW, lineColor: [0, 0, 0], minCellHeight: 12, cellPadding: 3 },
    headStyles: {
      fillColor: false, textColor: [0, 0, 0], fontStyle: 'bolditalic',
      fontSize: 8, halign: 'center', lineWidth: LW, lineColor: [0, 0, 0], minCellHeight: 10,
    },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: cw - 30 - 32 - 24 - 28 },
      2: { halign: 'right', cellWidth: 32 },
      3: { halign: 'right', cellWidth: 24 },
      4: { halign: 'right', cellWidth: 28 },
    },
    margin: { left: M, right: M, top: borderStartY },
  });

  y = doc.lastAutoTable.finalY;

  // ── Compute footer section values ───────────────────────────────────
  let totalUsd = parseFloat(document.total_usd) || 0;
  if (totalUsd === 0) {
    items.forEach(it => { totalUsd += parseFloat(it.amount_usd) || 0; });
  }

  // Every line item's packages added up against the one fixed unit: "8 PKGS"
  const pkgsLine = buildPackagesLine(document, items);
  const netWt = document.total_net_weight || items.reduce((s, i) => s + (parseFloat(i.net_weight_kg) || 0), 0);
  const grossWt = document.total_gross_weight || items.reduce((s, i) => s + (parseFloat(i.gross_weight_kg) || 0), 0);

  const amtWords = amountInWords(document, totalUsd);
  const amtLv = splitLabelValue(doc, 'Amount Chargeable (in words) ', amtWords, cw * 0.7 - 4);
  const amtH = Math.max(amtLv.lineCount * LH + 5, 8);

  // Declaration box — Other Reference printed verbatim (line breaks kept), carrying the
  // insurance cover-note line unless the declaration block above took it.
  doc.setFontSize(7);
  const declLines = doc.splitTextToSize(appendRegistrationLine(refText, document), cw - 4);
  const declH = Math.max(declLines.length * 3 + 4, 10);

  // Footer section heights
  const sigH = 22;
  const weightH = 16; // Total + Net + Gross + gap
  const totalFooterH = weightH + amtH + declH + sigH;

  // Position footer content at bottom of page (within border)
  const footerY = ph - M - totalFooterH;
  if (footerY > y + 5) {
    y = footerY;
  }

  // ── Totals & Weight Info ──────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont(FONT, 'bold');
  if (pkgsLine) doc.text(`Total : ${pkgsLine}`, M + 2, y);
  y += 4;
  doc.text(`Net Weight : ${(parseFloat(netWt) || 0).toFixed(3)} KGS.`, M + 2, y);
  y += 4;
  doc.text(`Gross Weight : ${(parseFloat(grossWt) || 0).toFixed(3)} KGS.`, M + 2, y);
  y += 6;

  // ── Amount Chargeable (in words) ────────────────────────────────────
  doc.rect(M, y, cw * 0.7, amtH);
  drawLabelValue(doc, amtLv, M + 2, y + 5);

  doc.rect(M + cw * 0.7, y, cw * 0.3, amtH);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text(
    `Total Value(USD): ${totalUsd.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    M + cw * 0.7 + 2, y + 5
  );
  y += amtH;

  // ── Declaration / Insurance ─────────────────────────────────────────
  doc.rect(M, y, cw, declH);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(7);
  doc.text(declLines, M + 2, y + 4);
  y += declH;

  // ── Declarations + Signature Row ────────────────────────────────────
  doc.rect(M, y, halfW, sigH);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text('Declarations', M + 2, y + 5);
  doc.setFont(FONT, 'italic');
  doc.setFontSize(7);
  doc.text('We hereby declare that this Invoice shown the Actual Price', M + 2, y + 9);
  doc.text('of the goods described and all particulars are true and', M + 2, y + 13);
  doc.text('correct.', M + 2, y + 17);

  doc.rect(M + halfW, y, halfW, sigH);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  const compName = sv(company.exporter_name, '').toUpperCase();
  doc.text(`For, ${compName}`, M + halfW + 2, y + 5);
  if (authority) {
    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.text(
      `${sv(authority.authority_type, 'Director')}/ Authorised Signatory`,
      pw - M - 2, y + sigH - 3, { align: 'right' }
    );
  }

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    // Page border (starts below the title)
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(M, borderStartY, pw - 2 * M, ph - borderStartY - M);

    // Title on every page (above the border)
    doc.setFontSize(14);
    doc.setFont(FONT, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('C O M M E R C I A L   I N V O I C E', pw / 2, M + 5, { align: 'center' });

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    // Left: Date
    doc.text(footerDate, M, ph - 5);

    // Center: Page number
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });

    // Right: Company name
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`Invoice_${sv(document.invoice_no, document.id)}.pdf`);
}
